import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useSEO } from "@/hooks/useSEO";
import { Overline, Breadcrumbs } from "@/components/shared";

const TYPES = ["Article / Topic", "AI Tool", "AI Model", "Technology / Product", "Gaming",
  "Review", "Guide / Tutorial", "Deal / Product", "Website Feature", "Correction / Fact Check", "Other"];

const WHAT_YOU_CAN = [
  "Stories & topics worth covering",
  "AI tools or models we should review",
  "Games and technology we should cover",
  "Guides and tutorials you'd like to see",
  "Deals or products worth highlighting",
  "Features you'd like KLYROO to build",
  "Corrections or factual issues",
  "Ideas to improve KLYROO",
];

const EMPTY = { suggestion_type: "", suggestion: "", reason: "", reference_url: "", name: "", email: "", website: "" };
const inputCls = "w-full bg-klyroo-surface border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-klyroo-cyan/50 transition-colors";

export default function Suggest() {
  useSEO({ title: "Suggest to KLYROO", description: "Have an idea, topic, tool, feature, correction or recommendation for KLYROO? Tell us — your suggestions shape what we cover, build and improve." });
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const validate = () => {
    const e = {};
    if (!form.suggestion_type) e.suggestion_type = "Please choose a suggestion type.";
    if (!form.suggestion.trim()) e.suggestion = "Please enter your suggestion.";
    else if (form.suggestion.length > 5000) e.suggestion = "Too long (max 5000 characters).";
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Enter a valid email.";
    if (form.reference_url.trim() && !/^https?:\/\/[^\s]+\.[^\s]+/.test(form.reference_url.trim())) e.reference_url = "Enter a valid URL (starting with http).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await api.submitSuggestion(form);
      // Only count a genuinely created suggestion — not a duplicate/honeypot 200.
      if (res && res.created_new) track.suggestionSubmit(form.suggestion_type, res.id);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send your suggestion. Please try again.");
    } finally { setSending(false); }
  };

  const isCorrection = form.suggestion_type === "Correction / Fact Check";

  return (
    <div className="klyroo-radial">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Suggest" }]} />
        <Overline>Get involved</Overline>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Help Shape KLYROO</h1>
        <p className="text-slate-300 mt-4 text-lg">Have an idea, topic, tool, feature, correction, or recommendation for KLYROO? We'd love to hear it.</p>
        <p className="text-slate-400 mt-2 text-sm">KLYROO is built to discover, explain, and surface the technology that matters. Your suggestions help us decide what to cover, build, test, and improve.</p>

        {done ? (
          <div className="mt-8 card-surface rounded-2xl p-8 text-center" data-testid="suggest-success">
            <CheckCircle2 size={44} className="text-klyroo-cyan mx-auto" />
            <h2 className="font-display text-2xl font-bold text-white mt-4">Thanks for the suggestion!</h2>
            <p className="text-slate-300 mt-3">Your idea has been sent to the KLYROO team. We appreciate you helping us make KLYROO better.</p>
            <p className="text-slate-500 mt-2 text-sm">We review suggestions regularly and use them to guide future coverage, features, and improvements.</p>
            <button data-testid="suggest-again" onClick={() => { setForm(EMPTY); setDone(false); }}
              className="mt-6 px-6 py-3 rounded-xl bg-klyroo-cyan text-black font-semibold hover:bg-white transition-colors">Send Another Suggestion</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="mt-8 card-surface rounded-2xl p-6 sm:p-8 space-y-5">
            <Field label="What would you like to suggest?" required error={errors.suggestion_type}>
              <select data-testid="suggest-type" value={form.suggestion_type} onChange={(e) => set("suggestion_type", e.target.value)} className={inputCls}>
                <option value="">Choose a category…</option>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>

            <Field label="Your suggestion" required error={errors.suggestion}>
              <textarea data-testid="suggest-text" rows={5} value={form.suggestion} onChange={(e) => set("suggestion", e.target.value)}
                placeholder={isCorrection ? "Found something inaccurate or outdated on KLYROO? Tell us what needs correcting and include a reliable reference if possible." : "Tell us what you think KLYROO should cover, add, review, build, or improve..."}
                className={inputCls} />
            </Field>

            <Field label="Why should KLYROO cover this?" hint="Optional">
              <textarea data-testid="suggest-reason" rows={3} value={form.reason} onChange={(e) => set("reason", e.target.value)}
                placeholder="Tell us why this would be useful or interesting to KLYROO readers." className={inputCls} />
            </Field>

            <Field label="Related link" hint="Optional" error={errors.reference_url}
              help="Have a website, article, product, tool, social post, or other reference? Add it here.">
              <input data-testid="suggest-url" type="url" value={form.reference_url} onChange={(e) => set("reference_url", e.target.value)} placeholder="https://…" className={inputCls} />
            </Field>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Your name" hint="Optional">
                <input data-testid="suggest-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={inputCls} />
              </Field>
              <Field label="Your email" hint="Optional" error={errors.email} help="Only provide your email if you'd like us to contact you about your suggestion.">
                <input data-testid="suggest-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={inputCls} />
              </Field>
            </div>

            {/* honeypot — hidden from users, catches bots */}
            <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => set("website", e.target.value)}
              className="absolute left-[-9999px] w-px h-px opacity-0" name="website" />

            <button type="submit" disabled={sending} data-testid="suggest-submit"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-klyroo-cyan text-black font-semibold hover:bg-white transition-colors disabled:opacity-50">
              <Send size={16} /> {sending ? "Sending…" : "Send Suggestion"}
            </button>
          </form>
        )}

        <div className="mt-10">
          <div className="flex items-center gap-2 text-klyroo-cyan"><Lightbulb size={16} /><span className="overline">What can you suggest?</span></div>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {WHAT_YOU_CAN.map((w) => (
              <li key={w} className="flex items-start gap-2 text-sm text-slate-300"><span className="text-klyroo-cyan mt-1">▹</span>{w}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, hint, help, error, children }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-slate-200 mb-1.5">
        {label} {required ? <span className="text-klyroo-cyan">*</span> : hint ? <span className="text-xs text-slate-500 font-normal">({hint})</span> : null}
      </label>
      {children}
      {help && !error && <p className="text-xs text-slate-500 mt-1.5">{help}</p>}
      {error && <p className="text-xs text-klyroo-red mt-1.5" data-testid="suggest-error">{error}</p>}
    </div>
  );
}
